import React, { useEffect, useState, useCallback } from "react";
import SuperAdminLayout from "@/components/SuperAdminLayout";
import api, { formatApiError, API } from "@/lib/api";
import { getAuthToken } from "@/lib/api";
import { toast } from "sonner";
import {
  Search,
  Filter,
  Download,
  X,
  ChevronLeft,
  ChevronRight,
  FileBarChart,
  AlertTriangle,
} from "lucide-react";

/**
 * v1.2 PR-B.1 — Cross-org audit log viewer.
 *
 * Backend: GET /api/super-admin/audit-log?org_id&actor_user_id&action&target_type&from_date&to_date&limit&offset
 *   - Returns array body; pagination via X-Total-Count / X-Limit / X-Offset response headers.
 *
 * CSV export: GET /api/super-admin/audit-log/export.csv?...filters (50k row cap).
 *
 * Security-sensitive actions render with a red/amber colour treatment so the
 * operator sees them at a glance.
 */
const SENSITIVE_ACTIONS = new Set([
  "user.elevated_to_super_admin",
  "integration.test_send",
  "super_admin.impersonation_started",
  "super_admin.impersonation_ended",
  "user.email_changed",
  "user.password_changed",
  "integration.secret_set",
  "integration.secret_cleared",
]);

const PAGE_SIZE = 50;

export default function SuperAdminAuditLog() {
  const [orgs, setOrgs] = useState([]);
  const [filters, setFilters] = useState({
    org_id: "",
    actor_user_id: "",
    action: "",
    target_type: "",
    from_date: "",
    to_date: "",
  });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);

  // Load org list once for the filter dropdown
  useEffect(() => {
    api.get("/super-admin/orgs").then(({ data }) => setOrgs(data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get("/super-admin/audit-log", { params });
      setRows(res.data || []);
      const t = parseInt(res.headers?.["x-total-count"] || res.headers?.["X-Total-Count"] || "0", 10);
      setTotal(Number.isFinite(t) ? t : 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters, offset]);

  useEffect(() => { load(); }, [load]);

  const applyFilters = (next) => {
    setOffset(0);
    setFilters({ ...filters, ...next });
  };
  const clearFilters = () => {
    setOffset(0);
    setFilters({ org_id: "", actor_user_id: "", action: "", target_type: "", from_date: "", to_date: "" });
  };

  const exportCsv = () => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
    const url = `${API}/super-admin/audit-log/export.csv?${qs.toString()}`;
    const token = getAuthToken();
    // Use a fetch + blob URL so the Authorization header is sent (anchor downloads
    // can't carry headers). Keep the click target in the user gesture so popup
    // blockers don't intervene.
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(async (r) => {
        if (!r.ok) throw new Error(`Export failed (${r.status})`);
        const blob = await r.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        toast.success("CSV exported");
      })
      .catch((e) => toast.error(e.message || "Export failed"));
  };

  return (
    <SuperAdminLayout>
      <div data-testid="super-admin-audit-log-page" className="space-y-5">
        <header className="flex items-end justify-between gap-4">
          <div>
            <div className="eb-overline mb-1">Super admin</div>
            <h1 className="text-3xl font-semibold text-navy-900 dark:text-white inline-flex items-center gap-2">
              <FileBarChart className="w-7 h-7 text-orange-500" /> Audit log (cross-org)
            </h1>
            <p className="text-sm text-navy-500 dark:text-navy-300 mt-1">
              Forensic trail across every organisation. Security-sensitive actions are highlighted.
            </p>
          </div>
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium bg-orange-500 text-white hover:bg-orange-600"
            data-testid="audit-export-csv"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV ({total.toLocaleString()})
          </button>
        </header>

        <FilterBar
          orgs={orgs}
          filters={filters}
          onChange={applyFilters}
          onClear={clearFilters}
        />

        <ResultsTable
          rows={rows}
          loading={loading}
          total={total}
          offset={offset}
          onPage={setOffset}
          onRowClick={setDrawer}
        />
      </div>

      <Drawer row={drawer} onClose={() => setDrawer(null)} />
    </SuperAdminLayout>
  );
}

// -------------------- Filter bar --------------------
function FilterBar({ orgs, filters, onChange, onClear }) {
  const [local, setLocal] = useState(filters);
  useEffect(() => setLocal(filters), [filters]);

  const submit = (e) => {
    e?.preventDefault();
    onChange(local);
  };

  return (
    <form
      onSubmit={submit}
      className="bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-xl p-4 grid gap-3 md:grid-cols-6 items-end"
      data-testid="audit-filter-bar"
    >
      <div>
        <label className="eb-label">Organisation</label>
        <select
          value={local.org_id}
          onChange={(e) => setLocal({ ...local, org_id: e.target.value })}
          className="eb-input"
          data-testid="audit-filter-org"
        >
          <option value="">All</option>
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>
      <div className="md:col-span-2">
        <label className="eb-label">Action (substring)</label>
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-navy-400 absolute left-2.5 top-3" />
          <input
            value={local.action}
            onChange={(e) => setLocal({ ...local, action: e.target.value })}
            className="eb-input pl-8"
            placeholder="e.g. integration.test"
            data-testid="audit-filter-action"
          />
        </div>
      </div>
      <div>
        <label className="eb-label">Target type</label>
        <input
          value={local.target_type}
          onChange={(e) => setLocal({ ...local, target_type: e.target.value })}
          className="eb-input"
          placeholder="e.g. user"
          data-testid="audit-filter-target-type"
        />
      </div>
      <div>
        <label className="eb-label">From date</label>
        <input
          type="date"
          value={local.from_date}
          onChange={(e) => setLocal({ ...local, from_date: e.target.value })}
          className="eb-input"
          data-testid="audit-filter-from"
        />
      </div>
      <div>
        <label className="eb-label">To date</label>
        <input
          type="date"
          value={local.to_date}
          onChange={(e) => setLocal({ ...local, to_date: e.target.value })}
          className="eb-input"
          data-testid="audit-filter-to"
        />
      </div>
      <div className="md:col-span-6 flex justify-end gap-2">
        <button type="button" onClick={() => { onClear(); }} className="text-xs px-3 py-1.5 rounded-md border border-navy-200 hover:bg-navy-50" data-testid="audit-filter-clear">
          Clear
        </button>
        <button type="submit" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-navy-900 text-white hover:bg-navy-800" data-testid="audit-filter-apply">
          <Filter className="w-3.5 h-3.5" /> Apply filters
        </button>
      </div>
    </form>
  );
}

// -------------------- Table --------------------
function ResultsTable({ rows, loading, total, offset, onPage, onRowClick }) {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const current = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div className="bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="audit-results-table">
          <thead className="bg-navy-50/60 dark:bg-navy-900/40 text-xs text-navy-600 dark:text-navy-300 uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2">When</th>
              <th className="text-left px-4 py-2">Action</th>
              <th className="text-left px-4 py-2">Actor</th>
              <th className="text-left px-4 py-2">Target</th>
              <th className="text-left px-4 py-2">Org</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-navy-100">
            {loading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-navy-500">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-navy-400">No rows match the current filters.</td></tr>
            )}
            {!loading && rows.map((r) => {
              const sensitive = SENSITIVE_ACTIONS.has(r.action);
              return (
                <tr
                  key={r.id}
                  className={`cursor-pointer transition-colors ${sensitive ? "bg-warning/5 hover:bg-warning/10" : "hover:bg-navy-50/40"}`}
                  onClick={() => onRowClick(r)}
                  data-testid="audit-row"
                >
                  <td className="px-4 py-2 text-xs font-mono text-navy-700 whitespace-nowrap">
                    {r.at ? new Date(r.at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">
                    {sensitive && <AlertTriangle className="inline w-3.5 h-3.5 text-warning mr-1.5 -mt-0.5" />}
                    <span className={sensitive ? "text-warning-700 font-semibold" : "text-navy-800"}>{r.action}</span>
                  </td>
                  <td className="px-4 py-2 text-xs text-navy-700 truncate max-w-[180px]">
                    {r.user_id ? r.user_id.slice(0, 12) + "…" : <span className="text-navy-400">system</span>}
                  </td>
                  <td className="px-4 py-2 text-xs text-navy-700 truncate max-w-[180px]">
                    {r.target_type ? `${r.target_type}/${(r.target_id || "").slice(0, 10)}…` : "—"}
                  </td>
                  <td className="px-4 py-2 text-xs text-navy-500 truncate max-w-[180px]">
                    {r.org_id ? r.org_id.slice(0, 8) + "…" : <span className="text-navy-300">(global)</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <footer className="px-4 py-3 border-t border-navy-100 flex items-center justify-between text-xs text-navy-500" data-testid="audit-pagination">
        <span>Page {current} of {pages} · {total.toLocaleString()} rows</span>
        <div className="inline-flex gap-1">
          <button
            disabled={offset <= 0}
            onClick={() => onPage(Math.max(0, offset - PAGE_SIZE))}
            className="px-2 py-1 rounded border border-navy-200 hover:bg-navy-50 disabled:opacity-40"
            data-testid="audit-page-prev"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => onPage(offset + PAGE_SIZE)}
            className="px-2 py-1 rounded border border-navy-200 hover:bg-navy-50 disabled:opacity-40"
            data-testid="audit-page-next"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </footer>
    </div>
  );
}

// -------------------- Drawer (row details) --------------------
function Drawer({ row, onClose }) {
  if (!row) return null;
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="audit-drawer">
      <button onClick={onClose} className="flex-1 bg-black/40" aria-label="Close drawer" />
      <aside className="w-full md:w-[520px] bg-white shadow-xl overflow-y-auto">
        <header className="sticky top-0 bg-white px-5 py-4 border-b border-navy-100 flex items-center justify-between">
          <div>
            <div className="eb-overline">Audit row</div>
            <h2 className="text-sm font-mono text-navy-900">{row.action}</h2>
          </div>
          <button onClick={onClose} className="text-navy-400 hover:text-navy-700" data-testid="audit-drawer-close">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="px-5 py-4 space-y-3 text-sm">
          <RowField label="When" value={row.at ? new Date(row.at).toLocaleString() : "—"} mono />
          <RowField label="Actor user id" value={row.user_id || "system"} mono />
          <RowField label="Organisation" value={row.org_id || "(global)"} mono />
          <RowField label="Target" value={row.target_type ? `${row.target_type} / ${row.target_id}` : "—"} mono />
          <div>
            <div className="eb-overline mb-1">Metadata</div>
            <pre className="bg-navy-50 border border-navy-100 rounded p-3 text-xs text-navy-800 overflow-x-auto">
              {JSON.stringify(row.metadata || {}, null, 2)}
            </pre>
          </div>
        </div>
      </aside>
    </div>
  );
}

function RowField({ label, value, mono }) {
  return (
    <div>
      <div className="eb-overline mb-0.5">{label}</div>
      <div className={`text-navy-900 ${mono ? "font-mono text-xs" : "text-sm"} break-all`}>{value}</div>
    </div>
  );
}
